import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { IoCheckbox, IoSquareOutline, IoClose, IoRemove, IoAdd, IoInformationCircleOutline, IoCheckboxOutline, IoTrashOutline, IoReloadCircle, IoSwapHorizontal } from 'react-icons/io5';
import { useDispatch } from 'react-redux';
import { CartApiService } from '../../../service/cartApiService';
import { fetchCartItems } from '../../../../redux/slices/cartSlice';
import { getAuthLocalStorage } from '../../../../storage/localStorage';
import { appendSizeToImageUrl } from '../../../../utils/imageUtils';
import { PRICE_LOGIC, SellingModes } from '../../../../helper/constants';
import { ProductsApiService } from '../../../../service/website/productsApiService';
import { getPriceForQuantity, getTotalSelectedQuantity } from '../../../component/views/productDetails/utils';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import { generateProductUrl } from '../../../../utils/productUrlUtils';
import ChangeVariantModal from './ChangeVariantModal';
import './style.css';
import '../../productDetails/style.css';

// Helper to check if dropshipping is available for a product
const isDropshippingAvailableFromTags = (tagInfoList = []) => {
  const isOnePsaleTag = tagInfoList.find(tag => tag.key === 'isOnePsale');
  // Dropshipping is available if isOnePsale is not false
  return !(isOnePsaleTag?.value === false || isOnePsaleTag?.value === 'false');
};

const getEffectiveWholesaleMOQFromProduct = (product = {}, priceRanges = [], isWholesale = false) => {
  const parseQty = value => {
    const qty = Number(value);
    return Number.isFinite(qty) && qty > 0 ? qty : 0;
  };

  const isProductDropshippingAvailable = isDropshippingAvailableFromTags(product?.tagInfoList || []);
  
  // One-piece not available: actual product wholesale MOQ only (no min-2 from tiers)
  if (!isProductDropshippingAvailable) {
    const actualMOQ = [
      parseQty(product?.moq),
      parseQty(product?.minOrderQuantity),
      parseQty(product?.min_order_quantity),
      parseQty(product?.productSaleInfo?.minOrderQuantity),
    ].filter(Boolean);
    return actualMOQ.length ? Math.max(...actualMOQ) : 0;
  }

  const firstRangeMOQ = parseQty(
    priceRanges?.[0]?.startQuantity ??
    priceRanges?.[0]?.start_quantity ??
    product?.priceRanges?.[0]?.startQuantity ??
    product?.priceRanges?.[0]?.start_quantity
  );

  const candidates = [
    parseQty(product?.quantity),
    parseQty(product?.moq),
    parseQty(product?.minOrderQuantity),
    parseQty(product?.min_order_quantity),
    parseQty(product?.productSaleInfo?.minOrderQuantity),
    firstRangeMOQ,
  ].filter(Boolean);

  let minMOQ = candidates.length ? Math.max(...candidates) : 0;
  if (isWholesale && minMOQ === 1 && isProductDropshippingAvailable) minMOQ = 2;
  return minMOQ;
};

const VariantRow = ({
  variant,
  cartItemId,
  product,
  pricingMode,
  isMinMaxRange,
  isSelected,
  onSelect,
  onQuantityChange,
  isUpdating,
  isWholesale,
  isPendingSave,
  priceRanges,
  totalQuantity,
  selectedItems,
  fetchWholesalePricing,
  selectedVariants,
}) => {
  const [count, setCount] = useState(variant?.quantity || 0);
  const [showFullImage, setShowFullImage] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const debounceTimerRef = useRef(null);
  const isEditingRef = useRef(false);
  const dispatch = useDispatch();
  const message = useMessage();

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Initialize count from server on mount only
  useEffect(() => {
    setCount(variant?.quantity || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run only on mount to avoid resetting during user edits

  // Sync count when variant quantity changes from server, but not during user editing
  useEffect(() => {
    if (variant?.quantity !== undefined && !isEditingRef.current) {
      setCount(variant.quantity);
    }
  }, [variant?.quantity]);

  // Safety check
  if (!product || !variant || !variant.skuId) return null;

  // console.log('@@@@@@@@VARIANT: ', variant || {});
  const pricingBreakdown = variant?.breakdown || {};
  // console.log('@@@@@', pricingBreakdown.FBP)

  const basePrice = parseFloat(
    (isWholesale
      ? (variant?.pricing?.FBP || variant?.wholesaleFinalPrice || variant?.finalPrice)
      : variant?.finalPrice) ||
    variant?.basePrice ||
    variant?.price ||
    0
  );
  // console.log('got basePrice: ', basePrice);

  // Calculate ranged price for wholesale based on total quantity
  let rangedPrice = basePrice;
  let hasRangeDiscount = false;

  if (isWholesale && !isMinMaxRange && priceRanges && priceRanges.length > 0 && totalQuantity) {
    const sortedRanges = [...priceRanges].sort((a, b) => b.startQuantity - a.startQuantity);
    const activeRange = sortedRanges.find(range => totalQuantity >= range.startQuantity);
    if (activeRange && activeRange.finalPrice) {
      rangedPrice = parseFloat(activeRange.finalPrice);
      hasRangeDiscount = rangedPrice < basePrice;
    }
  }

  const finalPrice = isWholesale && !isMinMaxRange
    ? rangedPrice
    : parseFloat(variant?.wholesaleFinalPrice || variant?.finalPrice || 0);
  const itemQuantity = variant?.quantity || 0;

  const LS = pricingBreakdown.LS || 0;
  const CD = pricingBreakdown.CD || 0;
  const IF = pricingBreakdown.IF || 0;
  const LMD = pricingBreakdown.LMD || 0;
  const PF = pricingBreakdown.PF || 0;
  const GST_PR = pricingBreakdown.GST_PR || 0;
  const GST_FR = pricingBreakdown.GST_FR || 0;
  const GST_PF = pricingBreakdown.GST_PF || 0;

  const totalGST = GST_PR + GST_FR + GST_PF;
  const dutiesAndTaxes = LS + CD + IF + LMD + PF + totalGST;
  const totalPrice = finalPrice * itemQuantity;

  // Get minimum MOQ for this product
  // Special case: If MOQ is 1 and in wholesale mode with dropshipping available, convert to 2
  // const baseMOQ = product?.moq || product?.minOrderQuantity || 0;
  // const isProductDropshippingAvailable = isDropshippingAvailableFromTags(product?.tagInfoList || []);
  // const minMOQ = (baseMOQ === 1 && isWholesale && isProductDropshippingAvailable) ? 2 : baseMOQ;
  const minMOQ = getEffectiveWholesaleMOQFromProduct(product, priceRanges, isWholesale);

  // Immediate update (for onBlur)
  const handleQuantityChangeImmediate = newQty => {
    if (newQty < 1) return;
    if (newQty > (variant.amountOnSale || 0)) return;

    // For wholesale, check if total quantity meets MOQ
    if (isWholesale && minMOQ > 0) {
      // Calculate total quantity including this variant's new quantity
      const otherVariantsQty = (totalQuantity || 0) - count;
      const newTotalQty = otherVariantsQty + newQty;

      if (newTotalQty < minMOQ) {
        // If this is the only variant or reducing would go below MOQ, enforce minimum
        const requiredQty = Math.max(newQty, minMOQ - otherVariantsQty);
        if (requiredQty > newQty) {
          // Show warning and set to minimum required
          message.showToast(`Minimum order quantity is ${minMOQ}. Setting quantity to ${requiredQty}.`, ToastTypes.WARNING);
          setCount(requiredQty);
          // Clear existing debounce timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(async () => {
            const breakdown = await fetchWholesalePricing(product, cartItemId, requiredQty, variant);
            onQuantityChange(cartItemId, requiredQty, variant, pricingMode, breakdown);
          }, 1000);
          return;
        }
      }
    }

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    onQuantityChange(cartItemId, newQty, variant, pricingMode);
  };

  // Debounced update (for +/- buttons)
  const handleQuantityChange = newQty => {
    if (newQty < 1) return;
    if (newQty > (variant.amountOnSale || 0)) return;

    // For wholesale, check if total quantity meets MOQ
    if (isWholesale && minMOQ > 0) {
      // Calculate total quantity including this variant's new quantity
      const otherVariantsQty = (totalQuantity || 0) - count;
      const newTotalQty = otherVariantsQty + newQty;

      if (newTotalQty < minMOQ) {
        // If reducing would go below MOQ, prevent it
        message.showToast(`Minimum order quantity is ${minMOQ}. Cannot reduce below this limit.`, ToastTypes.WARNING);
        return;
      }
    }

    setCount(newQty);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    // Debounce API call - parent will handle pending save state
    debounceTimerRef.current = setTimeout(async () => {
      const breakdown = await fetchWholesalePricing(product, cartItemId, newQty, variant);
      onQuantityChange(cartItemId, newQty, variant, pricingMode, breakdown);
    }, 1000);
  };

  const handleRemove = async () => {
    try {
      await CartApiService.RemoveFromCart(
        JSON.stringify({
          productId: product._id,
          skuId: variant.skuId,
          pricingMode: pricingMode, // Include pricingMode for specific removal
        })
      );
      dispatch(fetchCartItems());
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const handleImageMouseEnter = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    setImagePosition({
      x: rect.right + 10, // 10px to the right of the image
      y: rect.top,
    });
    setShowFullImage(true);
  };

  const handleImageMouseLeave = () => {
    setShowFullImage(false);
  };

  // Check if this specific variant is selected (using unique key: cartItemId-skuId)
  const variantSelectionKey = `${cartItemId}-${variant.skuId}`;
  const isVariantSelected = selectedItems.has(variantSelectionKey);

  return (
    <div className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors relative variant-row-mobile">
      {/* Variant Image */}
      <div className="flex-shrink-0 relative">
        <img
          src={appendSizeToImageUrl(variant?.skuAttributes?.[0]?.skuImageUrl || variant?.image || product?.imgUrl?.[0], '100x100')}
          alt="Variant"
          referrerPolicy="no-referrer"
          className="w-[30px] h-[30px] object-cover rounded cursor-pointer variant-image-mobile"
          onMouseEnter={handleImageMouseEnter}
          onMouseLeave={handleImageMouseLeave}
        />
        {/* Full Image Preview */}
        {showFullImage && (
          <div
            className="fixed z-50 pointer-events-none full-image-preview-mobile"
            style={{
              left: `${imagePosition.x}px`,
              top: `${imagePosition.y}px`,
            }}
          >
            <img
              src={variant?.skuAttributes?.[0]?.skuImageUrl || variant?.image || product?.imgUrl?.[0]}
              alt="Full size"
              referrerPolicy="no-referrer"
              className="w-64 h-64 object-contain bg-white border-2 border-gray-300 rounded-lg shadow-2xl"
            />
          </div>
        )}
      </div>

      {/* Variant Details */}
      <div className="flex-1 min-w-0 variant-details-mobile">
        {variant?.skuAttributes && variant.skuAttributes.length > 0 && (
          <div className="space-y-1 variant-attributes-mobile">
            {variant.skuAttributes.map((attr, index) => (
              <div key={index} className="inline-block bg-gray-100 px-2 py-0.5 rounded text-[11px] text-gray-700 mr-1 mb-1 variant-attribute-mobile">
                <span className="font-medium">{attr.attributeNameTrans || attr.attributeName}:</span> <span>{attr.valueTrans || attr.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="text-[11px] text-gray-600 mt-1 variant-price-mobile">
          {(() => {
            // For 1-Piece/Dropshipping mode: Show pricingBreakdown.FBP directly
            if ((pricingMode === 'dropshipping' || pricingMode === '1-piece') && !isMinMaxRange) {
              const breakdownFBP = pricingBreakdown?.FBP ? Number(pricingBreakdown.FBP) : 0;
              if (breakdownFBP > 0) {
                return (
                  <div>
                    <span className="font-bold text-[#3DD0E5]">₹{breakdownFBP.toFixed(2)}</span>
                    <span className="text-gray-500">/piece</span>
                  </div>
                );
              }
            }

            if (isMinMaxRange) {
              // For min-max range mode: Show FBP from product variants pricing
              const variantIndex = selectedVariants.findIndex(v => v.skuId === variant.skuId);
              const productVariant = product?.variants?.[variantIndex >= 0 ? variantIndex : 0];
              const fbpPrice = productVariant?.pricing?.FBP ? Number(productVariant.pricing.FBP) : 0;

              if (fbpPrice > 0) {
                return (
                  <div>
                    <span className="font-bold text-[#3DD0E5]">₹{fbpPrice.toFixed(2)}</span>
                    <span className="text-gray-500">/piece</span>
                  </div>
                );
              }
            }

            // For Wholesale mode: Use priceRanges with strikethrough logic
            if (isWholesale && Array.isArray(priceRanges) && priceRanges.length > 1) {
              const currentQty = count || 0;

              // Sort ranges by startQuantity to find the highest applicable range
              const sortedRanges = [...priceRanges].sort((a, b) => a.startQuantity - b.startQuantity);

              // Find the highest applicable range for this variant's quantity
              let applicableRange = null;
              for (let i = sortedRanges.length - 1; i >= 0; i--) {
                if (currentQty >= sortedRanges[i].startQuantity) {
                  applicableRange = sortedRanges[i];
                  break;
                }
              }

              // Get the first (lowest MOQ) price range to compare against
              const firstRange = sortedRanges[0];
              const applicablePrice = applicableRange?.calculatedFBP ? Number(applicableRange.calculatedFBP) : 0;
              const firstRangePrice = firstRange?.calculatedFBP ? Number(firstRange.calculatedFBP) : 0;

              // Show strike-through pricing when quantity meets threshold and there's a lower price available
              if (applicableRange && applicablePrice !== undefined && currentQty >= applicableRange.startQuantity && applicablePrice < firstRangePrice) {
                return (
                  <div>
                    {firstRangePrice > 0 && <span className="text-gray-400 line-through mr-1">₹{firstRangePrice.toFixed(2)}</span>}
                    <span className="font-bold text-[#3DD0E5]">₹{applicablePrice.toFixed(2)}</span>
                    <span className="text-gray-500">/piece</span>
                  </div>
                );
              } else if (applicableRange && applicablePrice !== undefined) {
                // Quantity meets threshold but no lower price available, show normal price
                return (
                  <div>
                    <span className="font-bold text-[#3DD0E5]">₹{applicablePrice.toFixed(2)}</span>
                    <span className="text-gray-500">/piece</span>
                  </div>
                );
              } else {
                // Quantity doesn't meet any threshold, show original price
                // Use the first range price as fallback
                const fallbackPrice = firstRange?.calculatedFBP ? Number(firstRange.calculatedFBP) : 0;
                return (
                  <div>
                    <span className="font-bold text-[#3DD0E5]">₹{fallbackPrice.toFixed(2)}</span>
                    <span className="text-gray-500">/piece</span>
                  </div>
                );
              }
            }

            // Fallback: show base price
            return (
              <div>
                <span className="font-bold text-[#3DD0E5]">₹{(basePrice || finalPrice || 0).toFixed(2)}</span>
                <span className="text-gray-500">/piece</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Quantity Controls with Remove button above-right */}
      <div className="flex-shrink-0 relative quantity-remove-wrapper">
        {/* Remove button - positioned above-right */}
        <button
          onClick={handleRemove}
          className="absolute -top-2 -right-2 text-red-500 hover:text-red-700 bg-white rounded-full p-1 shadow-sm hover:shadow-md z-10 remove-btn-mobile"
        >
          <IoClose size={16} />
        </button>

        {/* Quantity Controls */}
        <div className="quantity-controls-mobile pt-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-gray-300 rounded-md quantity-input-wrapper-mobile">
              <button
                onClick={() => handleQuantityChange(count - 1)}
                disabled={count <= 1}
                className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 quantity-btn-mobile"
              >
                <IoRemove size={14} />
              </button>
              <input
                type="number"
                value={count}
                onFocus={() => {
                  isEditingRef.current = true;
                }}
                onChange={e => {
                  const inputVal = e.target.value;
                  // Allow empty input while typing
                  if (inputVal === '') {
                    setCount('');
                    // Clear any pending debounce when input is cleared
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }
                    return;
                  }
                  const val = parseInt(inputVal);
                  if (!isNaN(val) && val >= 0) {
                    // Update local state immediately for responsive UI
                    setCount(val);

                    // For valid quantities, trigger debounced update
                    if (val >= 1 && val <= (variant.amountOnSale || 0)) {
                      // Immediate validation for MOQ
                      if (isWholesale && minMOQ > 0) {
                        const otherVariantsQty = (totalQuantity || 0) - (variant?.quantity || 0);
                        const newTotalQty = otherVariantsQty + val;

                        if (newTotalQty < minMOQ) {
                          // Show warning but allow typing - will be corrected on blur if needed
                          message.showToast(`Minimum order quantity is ${minMOQ}. Current total will be ${newTotalQty}.`, ToastTypes.WARNING);
                        }
                      }

                      // Clear existing timer
                      if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                      }

                      // Debounce API call
                      debounceTimerRef.current = setTimeout(async () => {
                        // Final validation before API call - use variant.quantity (server value) for calculation
                        if (isWholesale && minMOQ > 0) {
                          const otherVariantsQty = (totalQuantity || 0) - (variant?.quantity || 0);
                          const newTotalQty = otherVariantsQty + val;

                          if (newTotalQty < minMOQ) {
                            const requiredQty = Math.max(val, minMOQ - otherVariantsQty);
                            message.showToast(`Minimum order quantity is ${minMOQ}. Setting quantity to ${requiredQty}.`, ToastTypes.WARNING);
                            setCount(requiredQty);
                            const breakdown = await fetchWholesalePricing(product, cartItemId, requiredQty, variant);
                            onQuantityChange(cartItemId, requiredQty, variant, pricingMode, breakdown);
                            return;
                          }
                        }

                        // Fetch pricing and update - val is the NEW quantity (SET operation, not ADD)
                        // console.log('Manual input - Sending quantity to API:', val, 'Original variant quantity:', variant?.quantity);
                        const breakdown = await fetchWholesalePricing(product, cartItemId, val, variant);
                        onQuantityChange(cartItemId, val, variant, pricingMode, breakdown);
                      }, 1000);
                    }
                  }
                }}
                onBlur={e => {
                  isEditingRef.current = false;
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val > 0) {
                    // Final validation on blur
                    if (isWholesale && minMOQ > 0) {
                      const otherVariantsQty = (totalQuantity || 0) - (variant?.quantity || 0);
                      const newTotalQty = otherVariantsQty + val;

                      if (newTotalQty < minMOQ) {
                        const requiredQty = Math.max(val, minMOQ - otherVariantsQty);
                        message.showToast(`Minimum order quantity is ${minMOQ}. Setting quantity to ${requiredQty}.`, ToastTypes.WARNING);
                        setCount(requiredQty);
                        // Clear any pending debounce and trigger immediate update
                        if (debounceTimerRef.current) {
                          clearTimeout(debounceTimerRef.current);
                        }
                        handleQuantityChangeImmediate(requiredQty);
                        return;
                      }
                    }
                    setCount(val);
                    // If debounce timer is still pending, let it complete
                    // Otherwise trigger immediate update
                    if (!debounceTimerRef.current) {
                      handleQuantityChangeImmediate(val);
                    }
                  } else {
                    setCount(itemQuantity);
                  }
                }}
                className="w-10 text-center border-0 outline-none text-[12px] quantity-input-mobile"
                disabled={false}
              />
              <button
                onClick={() => handleQuantityChange(count + 1)}
                disabled={count >= (variant.amountOnSale || 0)}
                className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 quantity-btn-mobile"
              >
                <IoAdd size={14} />
              </button>
            </div>
          </div>
          <div className="text-[10px] text-gray-500 mt-1 text-center stock-info-mobile">{variant.amountOnSale || 0} in stock</div>
        </div>
      </div>
    </div>
  );
};

// Memoize VariantRow to prevent unnecessary re-renders
const MemoizedVariantRow = React.memo(VariantRow, (prevProps, nextProps) => {
  // Only re-render if these specific values change
  return (
    prevProps.variant.skuId === nextProps.variant.skuId &&
    prevProps.variant.quantity === nextProps.variant.quantity &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isUpdating === nextProps.isUpdating &&
    prevProps.totalQuantity === nextProps.totalQuantity
  );
});

const CartProductCard = ({
  product,
  pricingMode,
  isMinMaxRange,
  priceRanges = [],
  variants,
  cartItemId,
  selectedItems,
  onSelect,
  onQuantityChange,
  updatingVariant,
  pendingSaveVariants = new Set(),
  setPriceSummary,
  setAllLoadingCard,
  cartAllProductsMOQRequiredRef,
  breakdown
}) => {
  const [showFullTitle, setShowFullTitle] = useState(false);
  const [showChargeDetails, setShowChargeDetails] = useState(false);
  const [titleExceedsOneLine, setTitleExceedsOneLine] = useState(false);
  const [showProductImage, setShowProductImage] = useState(false);
  const [productImagePosition, setProductImagePosition] = useState({ x: 0, y: 0 });
  const [pricingData, setPricingData] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [showMobilePriceDetails, setShowMobilePriceDetails] = useState(false);
  const titleRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isFirstMountRef = useRef(true);
  const dispatch = useDispatch();
  const authData = getAuthLocalStorage();
  const isLoggedIn = authData.isLoggedIn && authData.loginHeader;
  const authToken = authData.loginHeader;

  const [showMinMOQText, setShowMinMOQText] = useState(false);
  const [showChangeVariantModal, setShowChangeVariantModal] = useState(false);

  useEffect(() => {
    cartAllProductsMOQRequiredRef.current?.has(product.sku) ? setShowMinMOQText(true) : setShowMinMOQText(false);
  }, []);
  // Check if THIS specific cart item is being updated
  const isAnyVariantSaving = pendingSaveVariants.has(cartItemId) || updatingVariant === cartItemId;

  // Check if title exceeds one line
  useEffect(() => {
    if (titleRef.current && product?.title) {
      const lineHeight = parseInt(window.getComputedStyle(titleRef.current).lineHeight);
      const height = titleRef.current.scrollHeight;
      setTitleExceedsOneLine(height > lineHeight * 1.5);
    }
  }, [product?.title]);

  // Calculate totals for selected variants - MUST be before early return
  const isWholesale = pricingMode === SellingModes.Wholesale;

  // Check which variants are selected (using unique keys: cartItemId-skuId)
  const selectedVariants = useMemo(() => {
    if (!variants || variants.length === 0) return [];
    return variants.filter(v => selectedItems.has(`${cartItemId}-${v.skuId}`));
  }, [variants, cartItemId, selectedItems]);

  // Check if all variants are selected
  const allVariantsSelected = variants.length > 0 && variants.every(v => selectedItems.has(`${cartItemId}-${v.skuId}`));
  const someVariantsSelected = variants.some(v => selectedItems.has(`${cartItemId}-${v.skuId}`)) && !allVariantsSelected;
  const isSelected = allVariantsSelected;

  // Don't show price details if no variants are selected
  const hasSelection = selectedVariants.length > 0;

  // IMPORTANT: Calculate total quantity from selected variants (server-side quantities)
  const totalQuantity = useMemo(() => {
    if (!selectedVariants || selectedVariants.length === 0) return 0;
    return selectedVariants.reduce((sum, v) => {
      const qty = v?.quantity || 0;
      return sum + qty;
    }, 0);
  }, [selectedVariants]);

  const grossTotal = useMemo(() => {
    if (!selectedVariants || selectedVariants.length === 0) return 0;
    return selectedVariants.reduce((sum, v) => {
      const quantity = v?.quantity || 0;
      const price = isWholesale
        ? parseFloat(v?.wholesaleFinalPrice || v?.finalPrice || v?.price || 0)
        : parseFloat(v?.breakdown?.FBP || v?.finalPrice || v?.price || 0);
      return sum + price * quantity;
    }, 0);
  }, [selectedVariants, isWholesale]);

  const totalPrice = useMemo(() => {
    if (!selectedVariants || selectedVariants.length === 0) return 0;
    // Wholesale: prefer API computed FBP; fallback to ranged price calc
    if (isWholesale) {
      if (pricingData?.FBP) {
        return parseFloat(pricingData.FBP || 0);
      }
      // Fallback: compute using range price based on totalQuantity
      const total = selectedVariants.reduce((sum, v) => {
        const quantity = v?.quantity || 0;
        if (quantity <= 0) return sum;
        const originalPrice = parseFloat(
          v.wholesaleFinalPrice ||
          v.cp ||
          v.consignPrice ||
          v.fenxiaoPriceInfo?.offerPrice ||
          v.ap ||
          v.price ||
          0
        );
        let perPiece = originalPrice;
        if (priceRanges && priceRanges.length > 0 && totalQuantity > 0) {
          const rangePrice = getPriceForQuantity(originalPrice, totalQuantity, priceRanges);
          perPiece = rangePrice && (rangePrice.finalPrice ?? rangePrice.price) ? parseFloat(rangePrice.finalPrice ?? rangePrice.price) : originalPrice;
        }
        return sum + perPiece * quantity;
      }, 0);
      return total;
    }
    // Dropshipping: use FBP when available per variant, otherwise fall back to finalPrice/price
    return selectedVariants.reduce((sum, v) => {
      const quantity = v?.quantity || 0;
      if (quantity <= 0) return sum;
      const perPiece = parseFloat(v?.breakdown?.FBP ?? v?.finalPrice ?? v?.price ?? 0);
      return sum + perPiece * quantity;
    }, 0);
  }, [selectedVariants, isWholesale, pricingData, priceRanges, totalQuantity]);

  const discountAmount = grossTotal - totalPrice;
  const dropshipBreakdownTotals = useMemo(() => {
    if (isWholesale || !selectedVariants || selectedVariants.length === 0) return null;
    return selectedVariants.reduce(
      (acc, v) => {
        const breakdown = v?.breakdown || {};
        const qty = v?.quantity || 1;
        const getNum = val => {
          const num = parseFloat(val);
          return isNaN(num) ? 0 : num;
        };
        acc.AP_RMB += getNum(breakdown.AP_RMB) * qty;
        acc.AP_INR += getNum(breakdown.AP_INR) * qty;
        acc.MP += getNum(breakdown.MP) * qty;
        acc.CP += getNum(breakdown.CP) * qty;
        acc.LS += getNum(breakdown.LS) * qty;
        acc.CD += getNum(breakdown.CD) * qty;
        acc.IF += getNum(breakdown.IF) * qty;
        acc.LMD += getNum(breakdown.LMD) * qty;
        acc.LC += getNum(breakdown.LC) * qty;
        acc.PF += getNum(breakdown.PF) * qty;
        acc.GST_PR += getNum(breakdown.GST_PR) * qty;
        acc.GST_FR += getNum(breakdown.GST_FR) * qty;
        acc.GST_PF += getNum(breakdown.GST_PF) * qty;
        acc.FBP += getNum(breakdown.FBP) * qty;
        return acc;
      },
      { AP_RMB: 0, AP_INR: 0, MP: 0, CP: 0, LS: 0, CD: 0, IF: 0, LMD: 0, LC: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, FBP: 0 }
    );
  }, [selectedVariants, isWholesale]);

  const pricingBreakdown = breakdown || (isWholesale && pricingData ? pricingData : dropshipBreakdownTotals || {});
  const LS2 = pricingBreakdown.LS || 0;
  const CD2 = pricingBreakdown.CD || 0;
  const IF2 = pricingBreakdown.IF || 0;
  const LMD2 = pricingBreakdown.LMD || 0;
  const PF2 = pricingBreakdown.PF || 0;
  const GST_PR2 = pricingBreakdown.GST_PR || 0;
  const GST_FR2 = pricingBreakdown.GST_FR || 0;
  const GST_PF2 = pricingBreakdown.GST_PF || 0;
  const totalGST2 = GST_PR2 + GST_FR2 + GST_PF2;
  const dutiesAndTaxes2 = LS2 + CD2 + IF2 + LMD2 + PF2 + totalGST2;

  const updateDropshippingPriceSummary = useCallback(() => {
    if (typeof setPriceSummary !== 'function') return;
    if (isWholesale) return;

    const variantsToUse = variants || [];
    let breakdownTotals = dropshipBreakdownTotals;

    if (!breakdownTotals && variantsToUse.length > 0) {
      breakdownTotals = variantsToUse.reduce(
        (acc, v) => {
          const breakdown = v?.breakdown || {};
          const qty = v?.quantity || 1;
          const getNum = val => {
            const num = parseFloat(val);
            return isNaN(num) ? 0 : num;
          };
          acc.LS += getNum(breakdown.LS) * qty;
          acc.CD += getNum(breakdown.CD) * qty;
          acc.IF += getNum(breakdown.IF) * qty;
          acc.LMD += getNum(breakdown.LMD) * qty;
          acc.PF += getNum(breakdown.PF) * qty;
          acc.GST_PR += getNum(breakdown.GST_PR) * qty;
          acc.GST_FR += getNum(breakdown.GST_FR) * qty;
          acc.GST_PF += getNum(breakdown.GST_PF) * qty;
          acc.FBP += getNum(breakdown.FBP) * qty;
          return acc;
        },
        { LS: 0, CD: 0, IF: 0, LMD: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, FBP: 0 }
      );
    }

    // If still no breakdown, create empty one
    if (!breakdownTotals) {
      breakdownTotals = { LS: 0, CD: 0, IF: 0, LMD: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, FBP: 0 };
    }

    // For dropshipping, calculate TOTAL_PRICE thoroughly
    // Priority 1: Use FBP from breakdownTotals (sum of all variant FBP)
    let dropshipTotalPrice = parseFloat(breakdownTotals.FBP || 0);

    // Priority 2: If FBP is 0 or not available, calculate from individual variants' FBP
    if (!dropshipTotalPrice || dropshipTotalPrice === 0) {
      dropshipTotalPrice = variantsToUse.reduce((sum, v) => {
        const quantity = v?.quantity || 0;
        if (quantity <= 0) return sum;
        // Use FBP from breakdown (includes all duties/taxes), or finalPrice, or price
        const perPiece = parseFloat(v?.breakdown?.FBP ?? v?.finalPrice ?? v?.price ?? 0);
        return sum + perPiece * quantity;
      }, 0);
    }

    // Priority 3: If still 0, use the totalPrice from useMemo (which also calculates from variants)
    if (!dropshipTotalPrice || dropshipTotalPrice === 0) {
      dropshipTotalPrice = parseFloat(totalPrice || 0);
    }

    // Ensure we have a valid number
    dropshipTotalPrice = isNaN(dropshipTotalPrice) ? 0 : dropshipTotalPrice;

    const snapshot = {
      LS: parseFloat(breakdownTotals.LS || 0),
      CD: parseFloat(breakdownTotals.CD || 0),
      IF: parseFloat(breakdownTotals.IF || 0),
      LMD: parseFloat(breakdownTotals.LMD || 0),
      PF: parseFloat(breakdownTotals.PF || 0),
      GST_PR: parseFloat(breakdownTotals.GST_PR || 0),
      GST_FR: parseFloat(breakdownTotals.GST_FR || 0),
      GST_PF: parseFloat(breakdownTotals.GST_PF || 0),
      TOTAL_PRICE: dropshipTotalPrice,
    };

    setPriceSummary(prev => {
      const prevEntries = prev?.entries || {};
      const newEntries = { ...prevEntries, [cartItemId]: snapshot };

      // Recompute combined totals - sum all entries thoroughly
      const combined = Object.values(newEntries).reduce(
        (acc, e) => {
          if (e && typeof e === 'object') {
            const getNum = val => {
              const num = parseFloat(val);
              return isNaN(num) ? 0 : num;
            };
            acc.LS += getNum(e.LS);
            acc.CD += getNum(e.CD);
            acc.IF += getNum(e.IF);
            acc.LMD += getNum(e.LMD);
            acc.PF += getNum(e.PF);
            acc.GST_PR += getNum(e.GST_PR);
            acc.GST_FR += getNum(e.GST_FR);
            acc.GST_PF += getNum(e.GST_PF);
            acc.TOTAL_PRICE += getNum(e.TOTAL_PRICE);
          }
          return acc;
        },
        { LS: 0, CD: 0, IF: 0, LMD: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, TOTAL_PRICE: 0 }
      );

      return { ...combined, entries: newEntries };
    });
  }, [isWholesale, dropshipBreakdownTotals, totalPrice, cartItemId, setPriceSummary, variants]);

  // Report this card's snapshot for dropshipping mode only (wholesale uses fetchWholesalePricing)
  useEffect(() => {
    // For wholesale, skip this - fetchWholesalePricing handles it
    if (isWholesale) return;

    // Always update for dropshipping to ensure entry exists in priceSummary
    // This ensures dropshipping products are included in entriesSource
    updateDropshippingPriceSummary();
    // Note: Cleanup is handled by the universal cleanup effect below
  }, [isWholesale, updateDropshippingPriceSummary]);

  // Update dropshipping priceSummary when variant quantities change
  useEffect(() => {
    if (isWholesale) return;

    // Always update when quantities change to ensure entry is current
    updateDropshippingPriceSummary();
  }, [isWholesale, selectedVariants, totalQuantity, variants, updateDropshippingPriceSummary]);

  const fetchWholesalePricing = useCallback(
    async (productOverride = null, cartItemIdOverride = null, newQty = null, variantOverride = null) => {
      const productToUse = productOverride || product;
      const cartItemIdToUse = cartItemIdOverride || cartItemId;

      if (!isWholesale || !hasSelection || !totalQuantity || !isLoggedIn || !authToken) {
        setPricingData(null);
        return;
      }

      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      setLoadingPricing(true);
      setAllLoadingCard(prev => ({ ...prev, all: true, [cartItemIdToUse]: true }));
      let breakdown = null;
      try {
        // Build logistics SKU models from selected variants with actual quantities
        const logisticsSkuNumModels = selectedVariants
          .filter(v => v.quantity > 0 && v.skuId)
          .map(v => ({
            skuId: v.skuId,
            quantity: variantOverride && v.skuId === variantOverride.skuId && newQty !== null ? newQty : v.quantity,
          }));

        if (!logisticsSkuNumModels.length) {
          setLoadingPricing(false);
          setAllLoadingCard(prev => {
            const updated = { ...prev };
            delete updated[cartItemIdToUse];
            // Check if any card is still loading (excluding 'all' key)
            const loadingKeys = Object.keys(updated).filter(k => k !== 'all');
            updated.all = loadingKeys.length > 0 && loadingKeys.some(k => updated[k] === true);
            return updated;
          });
          return;
        }

        // Get offerId from product
        const offerId = productToUse?.offerId || productToUse?.offer_id || productToUse?.sku;
        if (!offerId) {
          setLoadingPricing(false);
          setAllLoadingCard(prev => {
            const updated = { ...prev };
            delete updated[cartItemIdToUse];
            // Check if any card is still loading (excluding 'all' key)
            const loadingKeys = Object.keys(updated).filter(k => k !== 'all');
            updated.all = loadingKeys.length > 0 && loadingKeys.some(k => updated[k] === true);
            return updated;
          });
          return;
        }

        // // Check if price logs exist for this offerId (for DB variant price based calculations)
        // let hasPriceLogs = false;
        // let dbVariantPrices = null;
        // try {
        //   const priceLogsRes = await ProductsApiService.checkPriceLogsForOffer(offerId, signal);
        //   if (signal.aborted) return;
        //   if (priceLogsRes?.status?.code === 200 && priceLogsRes?.data) {
        //     hasPriceLogs = priceLogsRes.data.hasPriceLogs || false;
        //     dbVariantPrices = priceLogsRes.data.variantPrices || null;
        //   }
        // } catch (err) {
        //   console.log('Failed to check price logs in cart:', err);
        // }

        // Fetch freight cost for CN Procurement
        let shippingCost = 0;
        // const freightResult = await ProductsApiService.getLocalShippingCost(parseFloat(offerId), logisticsSkuNumModels, signal);

        // if (signal.aborted) return;

        // if (freightResult.success) {
        //   shippingCost = freightResult.shippingCost;
        // }

        // // Calculate totals from selected variants - use actual quantities
        // const effectiveTotalQty = selectedVariants.reduce((sum, v) => {
        //   const qty = variantOverride && v.skuId === variantOverride.skuId && newQty !== null ? newQty : v.quantity;
        //   return sum + qty;
        // }, 0);

        // // Prefer product.priceRanges over cart item's priceRanges for reliability
        // // This ensures we use the most up-to-date price ranges from the product
        // const effectivePriceRanges = (productToUse?.priceRanges && productToUse.priceRanges.length > 0)
        //   ? productToUse.priceRanges
        //   : (priceRanges && priceRanges.length > 0 ? priceRanges : []);

        // const { totalVariantsBasePrice, totalVariantsWeight, totalVariantsVolume, totalVariants } = selectedVariants.reduce(
        //   (sum, v) => {
        //     const qty = variantOverride && v.skuId === variantOverride.skuId && newQty !== null ? newQty : v.quantity;
        //     if (qty > 0) {
        //       // Use prices from cart item variant (these are the original prices from PDP)
        //       // Priority: cp (consignPrice) > ap > fenxiaoPriceInfo > consignPrice > price
        //       // This matches PDP logic exactly
        //       const originalPrice = parseFloat(v.cp || v.consignPrice || v.ap || v.fenxiaoPriceInfo?.onePiecePrice || v.fenxiaoPriceInfo?.offerPrice || v.price || 0);
        //       let priceToUse = originalPrice;

        //       // Apply price ranges if applicable - match PDP logic exactly
        //       // IMPORTANT: Use .price (base RMB) field, not .finalPrice, for AP_RMB calculation
        //       // This applies to ALL products (with or without price logs) to ensure discounts work
        //       if (effectivePriceRanges.length > 0 && isWholesale && effectiveTotalQty > 0 && !isMinMaxRange) {
        //         const rangePriceObj = getPriceForQuantity(originalPrice, effectiveTotalQty, effectivePriceRanges);
        //         if (rangePriceObj !== null && rangePriceObj !== undefined) {
        //           // Use .price field (base RMB) for AP_RMB calculation, same as PDP
        //           priceToUse = rangePriceObj.price ? parseFloat(rangePriceObj.price) : originalPrice;
        //         } else if (effectivePriceRanges[0]?.price) {
        //           // Fallback to first range price if no match found
        //           priceToUse = parseFloat(effectivePriceRanges[0].price);
        //         }
        //       }

        //       return {
        //         totalVariants: sum.totalVariants + qty,
        //         totalVariantsBasePrice: sum.totalVariantsBasePrice + priceToUse * qty,
        //         // Use chargeableWeight for pricing if available, otherwise use weight
        //         totalVariantsWeight: sum.totalVariantsWeight + (v.chargeableWeight || v.weight || 0) * qty,
        //         totalVariantsVolume: sum.totalVariantsVolume + (v.height && v.width && v.length ? v.height * v.width * v.length * qty : 0),
        //       };
        //     }
        //     return sum;
        //   },
        //   { totalVariantsBasePrice: 0, totalVariantsWeight: 0, totalVariantsVolume: 0, totalVariants: 0 }
        // );

        // // IMPORTANT: To ensure discounts work the same for products with and without price logs:
        // // - When quantity > 1: ALWAYS use forward calculation with discounted AP (NEVER pass dbFinalPrice)
        // // - When quantity = 1: Can use reverse calculation if price logs exist (for DB price accuracy)
        // // This ensures quantity discounts always apply correctly, regardless of price logs

        // let shouldUseReverseCalculation = false;
        // let totalDbFinalPrice = 0;

        // // Simple rule: Only use reverse calculation when quantity is exactly 1
        // // For quantity > 1, always use forward calculation to ensure discounts apply
        // if (hasPriceLogs && dbVariantPrices && effectiveTotalQty === 1) {
        //   // Quantity is 1 - can use reverse calculation for DB price accuracy
        //   selectedVariants.forEach(v => {
        //     const qty = variantOverride && v.skuId === variantOverride.skuId && newQty !== null ? newQty : v.quantity;
        //     if (qty > 0 && dbVariantPrices[v.skuId]) {
        //       const dbVariant = dbVariantPrices[v.skuId];
        //       // Use wholesaleFinalPrice for wholesale mode
        //       const dbPrice = parseFloat(dbVariant.wholesaleFinalPrice || dbVariant.finalPrice || 0);
        //       totalDbFinalPrice += dbPrice * qty;
        //     }
        //   });
        //   shouldUseReverseCalculation = totalDbFinalPrice > 0;
        // }
        // For quantity > 1: Always use forward calculation (don't pass dbFinalPrice)
        // This ensures discounts work exactly the same way as products without price logs

        // console.log('[Cart Pricing] Recalculating - Total AP_RMB:', totalVariantsBasePrice, 'Quantity:', effectiveTotalQty, 'Using priceRanges:', effectivePriceRanges.length > 0, 'hasPriceLogs:', hasPriceLogs, 'totalDbFinalPrice:', totalDbFinalPrice, 'shouldUseReverse:', shouldUseReverseCalculation);

        // const calculationData = {
        //   ap: totalVariantsBasePrice,
        //   ls: shippingCost,
        //   hsn: null,
        //   hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
        //   dw: totalVariantsWeight || PRICE_LOGIC.DEFAULT_DW * totalVariants,
        //   vw: totalVariantsVolume / PRICE_LOGIC.VOLUME_FACTOR,
        //   categoryId: productToUse?.categoryId || '',
        //   // Only pass dbFinalPrice when quantity is exactly 1 (no discount scenario)
        //   // For quantity > 1, NEVER pass dbFinalPrice to ensure discounts apply correctly
        //   ...(shouldUseReverseCalculation && totalDbFinalPrice > 0 && effectiveTotalQty === 1 ? {
        //     dbFinalPrice: totalDbFinalPrice,
        //     pricingMode: 'wholesale',
        //     offerId: offerId,
        //     totalQuantity: effectiveTotalQty,
        //     logisticsSkuNumModels: logisticsSkuNumModels,
        //   } : {}),
        // };

        // Calculate pricing algorithm
        // const data = await ProductsApiService.calculatePricingAlgorithm(calculationData, authToken, signal);

        // if (signal.aborted) return;

        // if (data.status.code === 200 && data.data?.breakdown) {
        //   breakdown = data.data.breakdown;
        //   setPricingData(breakdown);

        //   // Update price summary with correct values
        //   if (typeof setPriceSummary === 'function') {
        //     // For wholesale, TOTAL_PRICE should be FBP from API (includes all duties and taxes)
        //     const wholesaleTotalPrice = parseFloat(breakdown.FBP || 0);

        //     // Ensure we have a valid number (use 0 if NaN, otherwise use the actual value)
        //     const validTotalPrice = isNaN(wholesaleTotalPrice) ? 0 : wholesaleTotalPrice;

        //     const snapshot = {
        //       LS: parseFloat(breakdown.LS || 0),
        //       CD: parseFloat(breakdown.CD || 0),
        //       IF: parseFloat(breakdown.IF || 0),
        //       LMD: parseFloat(breakdown.LMD || 0),
        //       PF: parseFloat(breakdown.PF || 0),
        //       GST_PR: parseFloat(breakdown.GST_PR || 0),
        //       GST_FR: parseFloat(breakdown.GST_FR || 0),
        //       GST_PF: parseFloat(breakdown.GST_PF || 0),
        //       TOTAL_PRICE: validTotalPrice,
        //     };

        //     setPriceSummary(prev => {
        //       const prevEntries = prev?.entries || {};
        //       const newEntries = { ...prevEntries, [cartItemIdToUse]: snapshot };

        //       // Recompute combined totals - sum all entries thoroughly
        //       const combined = Object.values(newEntries).reduce(
        //         (acc, e) => {
        //           if (e && typeof e === 'object') {
        //             const getNum = val => {
        //               const num = parseFloat(val);
        //               return isNaN(num) ? 0 : num;
        //             };
        //             acc.LS += getNum(e.LS);
        //             acc.CD += getNum(e.CD);
        //             acc.IF += getNum(e.IF);
        //             acc.LMD += getNum(e.LMD);
        //             acc.PF += getNum(e.PF);
        //             acc.GST_PR += getNum(e.GST_PR);
        //             acc.GST_FR += getNum(e.GST_FR);
        //             acc.GST_PF += getNum(e.GST_PF);
        //             acc.TOTAL_PRICE += getNum(e.TOTAL_PRICE);
        //           }
        //           return acc;
        //         },
        //         { LS: 0, CD: 0, IF: 0, LMD: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, TOTAL_PRICE: 0 }
        //       );

        //       return { ...combined, entries: newEntries };
        //     });
        //   }

        //   // On first mount, update cart with breakdown
        //   if (isFirstMountRef.current && breakdown) {
        //     isFirstMountRef.current = false;
        //     try {
        //       // Build variants array with updated quantities
        //       const updatedVariants = selectedVariants
        //         .filter(v => v.quantity > 0 && v.skuId)
        //         .map(v => {
        //           const qty = variantOverride && v.skuId === variantOverride.skuId && newQty !== null ? newQty : v.quantity;
        //           return { skuId: v.skuId, quantity: qty };
        //         });

        //       const productPayload = {
        //         offerId: productToUse?.sku || productToUse?.offerId || productToUse?.offer_id,
        //         variants: updatedVariants,
        //         pricingMode: 'wholesale',
        //         breakdown: breakdown,
        //         isFromCart: true,
        //         ...(typeof isMinMaxRange !== 'undefined' ? { isMinMaxRange } : {})
        //       };

        //       await CartApiService.AddUpdateCart(JSON.stringify(productPayload));
        //     } catch (error) {
        //       console.error('Error updating cart with breakdown:', error);
        //     }
        //   }
        // }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching wholesale pricing data:', error);
        }
      } finally {
        if (!signal.aborted) {
          setLoadingPricing(false);
          setAllLoadingCard(prev => {
            const updated = { ...prev };
            delete updated[cartItemIdToUse];
            // Check if any card is still loading (excluding 'all' key)
            const loadingKeys = Object.keys(updated).filter(k => k !== 'all');
            updated.all = loadingKeys.length > 0 && loadingKeys.some(k => updated[k] === true);
            return updated;
          });
        }
      }
      return breakdown;
    },
    [isWholesale, hasSelection, totalQuantity, isLoggedIn, authToken, selectedVariants, priceRanges, isMinMaxRange, product, cartItemId, setPriceSummary, setAllLoadingCard]
  );

  // Fetch pricing on mount and when dependencies change
  useEffect(() => {
    if (!isWholesale || !hasSelection || !totalQuantity) return;

    // Reset first mount flag when dependencies change significantly
    const timer = setTimeout(() => {
      fetchWholesalePricing();
    }, 500);

    return () => {
      clearTimeout(timer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isWholesale, hasSelection, totalQuantity, selectedVariants]);

  // Cleanup priceSummary entry on unmount for ALL cards (both dropshipping and wholesale)
  useEffect(() => {
    return () => {
      setPriceSummary(prev => {
        const prevEntries = prev?.entries || {};
        if (!prevEntries[cartItemId]) return prev || { entries: {} };
        const { [cartItemId]: _, ...rest } = prevEntries;

        const combined = Object.values(rest).reduce(
          (acc, e) => {
            if (e && typeof e === 'object') {
              const getNum = val => {
                const num = parseFloat(val);
                return isNaN(num) ? 0 : num;
              };
              acc.LS += getNum(e.LS);
              acc.CD += getNum(e.CD);
              acc.IF += getNum(e.IF);
              acc.LMD += getNum(e.LMD);
              acc.PF += getNum(e.PF);
              acc.GST_PR += getNum(e.GST_PR);
              acc.GST_FR += getNum(e.GST_FR);
              acc.GST_PF += getNum(e.GST_PF);
              acc.TOTAL_PRICE += getNum(e.TOTAL_PRICE);
            }
            return acc;
          },
          { LS: 0, CD: 0, IF: 0, LMD: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, TOTAL_PRICE: 0 }
        );

        return { ...combined, entries: rest };
      });
    };
  }, [cartItemId, setPriceSummary]);

  // Reset first mount flag when cartItemId changes (different product card)
  useEffect(() => {
    isFirstMountRef.current = true;
  }, [cartItemId]);

  // Handle product-level selection (select/deselect all variants)
  const handleProductSelect = () => {
    if (allVariantsSelected) {
      // Deselect all variants
      variants.forEach(v => {
        onSelect(cartItemId, v.skuId);
      });
    } else {
      // Select all variants
      variants.forEach(v => {
        const variantKey = `${cartItemId}-${v.skuId}`;
        if (!selectedItems.has(variantKey)) {
          onSelect(cartItemId, v.skuId);
        }
      });
    }
  };

  // Handle removing all variants of this product
  const handleRemoveProduct = async () => {
    try {
      // Remove all variants in this cart item
      await CartApiService.RemoveFromCart(
        JSON.stringify({
          productId: product._id,
          pricingMode: pricingMode, // Include pricingMode for specific removal
        })
      );
      dispatch(fetchCartItems());
    } catch (error) {
      console.error('Error removing product:', error);
    }
  };

  // Product image hover handlers
  const handleProductImageMouseEnter = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    setProductImagePosition({
      x: rect.right + 10, // 10px to the right of the image
      y: rect.top,
    });
    setShowProductImage(true);
  };

  const handleProductImageMouseLeave = () => {
    setShowProductImage(false);
  };

  // Get active price range for wholesale
  const getActivePriceRange = () => {
    if (!isWholesale || !product.priceRanges || product.priceRanges.length === 0) return null;

    const sortedRanges = [...product.priceRanges].sort((a, b) => b.startQuantity - a.startQuantity);
    const activeRange = sortedRanges.find(range => totalQuantity >= range.startQuantity);
    return activeRange;
  };

  const activePriceRange = getActivePriceRange();
  // Get first variant for base price calculation
  const firstVariant = variants && variants.length > 0 ? variants[0] : null;
  const effectiveBasePrice = activePriceRange && activePriceRange.price ? parseFloat(activePriceRange.price) : parseFloat(firstVariant?.price || 0);

  if (!product || !variants || variants.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-4 overflow-hidden relative cart-product-card-mobile">
      {(isAnyVariantSaving || loadingPricing) && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-lg">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-md">
            <IoReloadCircle size={20} className="text-blue-600 animate-spin" />
            <span className="text-[12px] text-gray-700 font-medium">Loading...</span>
          </div>
        </div>
      )}

      <div className="grid !gap-4 p-4 cart-product-grid-mobile">
        {/* Mobile Top Row: Image + Title in one row (only for mobile) */}
        <div className="mobile-top-row flex items-start gap-3 mb-2">
          {/* Product Image for Mobile */}
          <div className="flex-shrink-0">
            <img
              referrerPolicy="no-referrer"
              src={appendSizeToImageUrl(product?.imgUrl?.[0], '200x200')}
              alt={product?.title || product?.name}
              className="w-[70px] h-[70px] object-cover rounded cursor-pointer product-image-mobile"
              onMouseEnter={handleProductImageMouseEnter}
              onMouseLeave={handleProductImageMouseLeave}
            />
            {/* Full Product Image Preview */}
            {showProductImage && (
              <div
                className="fixed z-50 pointer-events-none full-product-image-preview-mobile"
                style={{
                  left: `${productImagePosition.x}px`,
                  top: `${productImagePosition.y}px`,
                }}
              >
                <img
                  referrerPolicy="no-referrer"
                  src={product?.imgUrl?.[0]}
                  alt="Full size"
                  className="w-64 h-64 object-contain bg-white border-2 border-gray-300 rounded-lg shadow-2xl"
                />
              </div>
            )}
          </div>

          {/* Product Title Section for Mobile */}
          <div className="flex-1 min-w-0 relative product-title-section-mobile">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] font-semibold leading-tight product-title-mobile">
                  <a href={generateProductUrl({ offer: product.offerId || product.sku, isAli: true, title: product?.title || '', variantId: product?.variations?.[0]?.skuId || null })} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    <span ref={titleRef} className={`${!showFullTitle ? 'line-clamp-2' : ''}`}>
                      {product?.title}
                    </span>
                  </a>
                </h3>
                {titleExceedsOneLine && (
                  <button onClick={() => setShowFullTitle(!showFullTitle)} className="text-blue-600 text-[11px] hover:underline mt-1 see-more-btn-mobile">
                    {showFullTitle ? 'Show less' : 'Show more'}
                  </button>
                )}
                {/* Change Variant Button for Mobile */}
                <button
                  onClick={() => setShowChangeVariantModal(true)}
                  className="mt-2 flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                >
                  <IoSwapHorizontal size={14} />
                  Change Variant
                </button>
              </div>
              <span
                className={`inline-block px-3 py-1 text-[11px] font-semibold rounded flex-shrink-0 mode-badge-mobile ${isWholesale ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}
              >
                {isWholesale ? 'Wholesale' : '1-Piece'}
              </span>
            </div>

            {/* Remove button positioned absolutely on mobile */}
            <button
              onClick={handleRemoveProduct}
              className="absolute top-0 right-0 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-300 rounded-lg transition-all duration-200 remove-product-btn-mobile"
            >
              <IoClose size={16} />
            </button>
          </div>
        </div>

        {/* Desktop/Tablet: Product Image Section */}
        <div className="desktop-image-section flex-shrink-0 flex flex-col gap-2 justify-start items-center relative">
          <img
            referrerPolicy="no-referrer"
            src={appendSizeToImageUrl(product?.imgUrl?.[0], '200x200')}
            alt={product?.title || product?.name}
            className="w-[70px] h-[70px] object-cover rounded cursor-pointer product-image-desktop"
            onMouseEnter={handleProductImageMouseEnter}
            onMouseLeave={handleProductImageMouseLeave}
          />
          <button
            onClick={handleRemoveProduct}
            className="w-full flex items-center justify-center gap-1 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-300 rounded-lg transition-all duration-200 text-sm font-medium remove-product-desktop"
          >
            <IoClose size={16} />
          </button>
        </div>

        {/* Product Info & Variants */}
        <div className="product-info-section-mobile">
          {/* Desktop/Tablet: Product Title Section */}
          <div className="desktop-title-section flex items-start gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] font-semibold leading-tight">
                <a href={generateProductUrl({ offer: product.offerId || product.sku, isAli: true, title: product?.title || '', variantId: product?.variations?.[0]?.skuId || null })} target="_blank" rel="noopener noreferrer" className="hover:underline">
                  <span ref={titleRef} className={`${!showFullTitle ? 'line-clamp-1' : ''}`}>
                    {product?.title}
                  </span>
                </a>
              </h3>
              {titleExceedsOneLine && (
                <button onClick={() => setShowFullTitle(!showFullTitle)} className="text-blue-600 text-[11px] hover:underline mt-1 see-more-btn-desktop">
                  {showFullTitle ? 'Show less' : 'Show more'}
                </button>
              )}
              {/* Change Variant Button */}
              <button
                onClick={() => setShowChangeVariantModal(true)}
                className="mt-2 flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-700 hover:underline transition-colors"
              >
                <IoSwapHorizontal size={14} />
                Change Variant
              </button>
            </div>
            <span
              className={`inline-block px-3 py-1 text-[11px] font-semibold rounded flex-shrink-0 mode-badge-desktop ${isWholesale ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                }`}
            >
              {isWholesale ? 'Wholesale' : '1-Piece'}
            </span>
          </div>

          {/* UPDATED PRICE RANGE SECTION - Matches the image format */}
          {isWholesale && !isMinMaxRange && priceRanges && priceRanges.length > 0 && (
            <div className="my-0 space-y-0.5 price-ranges-mobile">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-0 text-[12px] text-gray-600 price-range-row-mobile">
                {priceRanges.map((range, index) => {
                  const isActive = activePriceRange && range.startQuantity === activePriceRange.startQuantity;
                  const rangeEnd = index < priceRanges.length - 1 ? priceRanges[index + 1].startQuantity - 1 : null;

                  return (
                    <div key={`${range.startQuantity}-${index}`} className={`price-range-item-inline ${isActive ? 'text-green-600 font-medium' : ''}`}>
                      {rangeEnd ? (
                        <span>
                          {range.startQuantity}-{rangeEnd} pieces
                        </span>
                      ) : (
                        <span>≥ {range.startQuantity} pieces</span>
                      )}
                      <span className="ml-1">₹{parseFloat(range.finalPrice).toFixed(2)}/piece</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {showMinMOQText && <span className="text-[11px] text-red-500 leading-none mb-0.5"> Min MOQ({product.quantity || product.moq}) required. </span>}

          <div
            className={`border border-gray-200 rounded-lg overflow-hidden mt-3 variants-list-mobile ${variants.length > 2 ? 'max-h-[280px] overflow-y-auto custom-scrollbar' : ''}`}
          >
            {variants.map((variant, index) => {
              // Get the corresponding variant breakdown from pricingBreakdown
              const variantBreakdown = breakdown?.variants?.[index] || variant?.breakdown || variant?.pricingBreakdown;

              return (
                <MemoizedVariantRow
                  key={variant.skuId}
                  isMinMaxRange={isMinMaxRange}
                  variant={{
                    ...variant,
                    breakdown: variantBreakdown // Add the pricingBreakdown to variant
                  }}
                  cartItemId={cartItemId}
                  product={product}
                  pricingMode={pricingMode}
                  isSelected={false}
                  onSelect={onSelect}
                  onQuantityChange={onQuantityChange}
                  isUpdating={updatingVariant === cartItemId}
                  isWholesale={isWholesale}
                  isPendingSave={pendingSaveVariants.has(cartItemId)}
                  priceRanges={priceRanges?.length > 0 ? priceRanges : product?.priceRanges || []}
                  totalQuantity={totalQuantity}
                  selectedItems={selectedItems}
                  fetchWholesalePricing={fetchWholesalePricing}
                  selectedVariants={selectedVariants}
                />
              );
            })}
          </div>
        </div>

        {/* Charges Summary */}
        <div className="border-l border-gray-200 pl-4 relative charges-summary-mobile">
          <div className="py-0 border-t border-[#ffffff] total-section-mobile">
            <div className="main-total-section">
              <span className="text-[14px] font-semibold">Total : </span>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-[18px] font-bold text-[#3DD0E5]">{isWholesale && loadingPricing ? 'Calculating...' : `₹${totalPrice.toFixed(2)}`}</div>
                  {!loadingPricing && <div className="text-[11px] text-gray-500 mt-0.5">₹{(totalPrice / totalQuantity || 0).toFixed(2)}/piece</div>}
                </div>
                {hasSelection && (
                  <button
                    type="button"
                    className="total-section-toggle-btn ml-1 flex items-center justify-center w-6 h-6 border border-gray-300 rounded-full"
                    onClick={() => setShowMobilePriceDetails(prev => !prev)}
                  >
                    {showMobilePriceDetails ? <IoRemove size={12} /> : <IoAdd size={12} />}
                  </button>
                )}
              </div>
            </div>

            {!hasSelection && <div className="mt-3 p-3 text-center text-gray-500 text-[13px] italic no-selection-message-mobile">Price details will appear here.</div>}

            {hasSelection && (
              <div className={`mt-0 mobile-price-details ${showMobilePriceDetails ? 'mobile-price-details-open' : ''}`}>
                <div className="flex justify-between items-center mb-3 pb-2 border-b border-[#a9b4ca]">
                  <h4 className="text-[14px] font-semibold">Price Details</h4>
                </div>

                <div className="flex justify-between items-center mb-3 mode-display-mobile">
                  <span className="text-[13px] text-gray-600">Mode :</span>
                  <span className="text-[13px] font-medium capitalize">{isWholesale ? 'Wholesale' : '1-Piece'}</span>
                </div>

                <div className="p-3 rounded border border-gray-200 mb-3 quantity-section-mobile">
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-[11px] text-gray-500">Variants : </span>
                    <span className="text-[11px] font-medium">{variants.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[13px] text-gray-600">Total Qty : </span>
                    <span className="text-[14px] font-semibold">{hasSelection ? `${totalQuantity} units` : '0 units'}</span>
                  </div>
                  {(isWholesale || (pricingMode !== 'dropshipping' && !isWholesale && isMinMaxRange)) && (
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[13px] text-gray-600">Gross Total : </span>
                      <span className="text-[14px] font-semibold">{hasSelection ? `₹${(product?.totalGrossAmount || 0).toFixed(2)}` : '₹0.00'}</span>
                    </div>
                  )}
                  {(isWholesale || (pricingMode !== 'dropshipping' && !isWholesale && isMinMaxRange)) && (
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[13px] text-gray-600">Procurement Charges: </span>
                      <span className="text-[14px] font-semibold">
                        {hasSelection ? (() => {
                          const baseProcurementCharges = breakdown?.procurementCharges || 0;
                          const discountValue = breakdown?.discountValue || 0;
                          // If discount is negative, add its absolute value to procurement charges
                          const adjustedProcurementCharges = discountValue < 0
                            ? baseProcurementCharges + Math.abs(discountValue)
                            : baseProcurementCharges;
                          return `₹${adjustedProcurementCharges.toFixed(2)}`;
                        })() : '₹0.00'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Discount and Total Price Section - Modified for desktop layout */}
                <div className="discount-total-wrapper">
                  {isWholesale && breakdown?.discountValue > 0 && (
                    <div className="flex justify-between items-center mb-0 discount-section-mobile">
                      <span className="text-[13px] text-gray-600">Discount : </span>
                      <span className="text-[14px] font-semibold text-[#3DD0E5]">₹{breakdown?.discountValue.toFixed(2)}</span>
                    </div>
                  )}

                  {/* This is the total price that will show below discount in desktop view */}
                  <div className="desktop-total-price">
                    <div className="flex justify-between items-center border-t border-gray-200 pt-3 mt-2">
                      <span className="text-[14px] font-semibold">Total : </span>
                      <div className="text-right">
                        <div className="text-[18px] font-bold text-[#3DD0E5]">{isWholesale && loadingPricing ? 'Calculating...' : `₹${breakdown?.finalPrice.toFixed(2)}`}</div>
                        {!loadingPricing && <div className="text-[11px] text-gray-500 mt-0.5">₹{((breakdown?.finalPrice || 0) / totalQuantity).toFixed(2)}/piece</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Variant Modal */}
      <ChangeVariantModal
        isOpen={showChangeVariantModal}
        onClose={() => setShowChangeVariantModal(false)}
        product={product}
        currentVariants={variants}
        cartItemId={cartItemId}
        pricingMode={pricingMode}
      />
    </div>
  );
};

export default CartProductCard;
